import React, {useState, useEffect} from "react";
import {useTheme} from "@mui/material/styles";
import MDBox from "components/MDBox";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  AppBar,
  Card,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery
} from "@mui/material";
import {GET} from "APIclient";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import * as XLSX from "xlsx";
import {Search as SearchIcon, Download as DownloadIcon} from "@mui/icons-material";
import {Stack, Tooltip} from "@mui/material";
import {GridActionsCellItem} from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import BlockIcon from "@mui/icons-material/Block";
import {useNavigate} from "react-router-dom";
import {POST} from "APIclient";
import {formatDesignation} from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";

const rowLength = 20;

export default function EmployeeProbationList() {
  const theme = useTheme();
  const [empId, setEmpId] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setSize] = useState(rowLength);
  const navigate = useNavigate();
  const [empName, setEmpName] = useState("");
  const isMobile = useMediaQuery("(max-width: 480px)");
  const [searchName, setSearchName] = useState("");

  const columns = [
    {field: "id", headerName: "#", width: 30, align: "left", headerAlign: "left"},
    {
      field: "empName",
      headerName: "Employee Name",
      width: 150,
      align: "left",
      headerAlign: "left"
    },
    {field: "empId", headerName: "EMP ID", width: 80, align: "left", headerAlign: "left"},
    {
      field: "probationStartDate",
      headerName: "Start Date",
      width: 140,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "probationEndDate",
      headerName: "End Date",
      width: 140,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 180,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: params => {
        const status = params.value.toLowerCase();
        let buttonColor;
        let statusText;

        switch (status) {
          case "pending":
            buttonColor = "warning";
            statusText = "Pending";
            break;
          case "completed":
            buttonColor = "success";
            statusText = "Completed";
            break;
        }

        return (
          <MDButton
            variant="contained"
            color={buttonColor}
            size="small"
            disabled
            sx={{
              textTransform: "capitalize",
              fontSize: "0.700rem",
              width: "60px",
              justifyContent: "center"
            }}
          >
            {statusText}
          </MDButton>
        );
      }
    },
    {
      field: "action",
      headerName: "Action",
      type: "icon",
      width: 120,
      headerAlign: "left",
      align: "left",
      renderCell: params => {
        const isCompleted = params.row.status.toLowerCase() === "completed";
        return (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="left"
            alignItems="left"
            sx={{width: "100%", height: "100%"}}
          >
            <Tooltip title="Extend Probation" arrow>
              <span>
                <GridActionsCellItem
                  key={`extend-${params.id}`}
                  icon={<HourglassTopIcon />}
                  label="Extend"
                  color="warning"
                  disabled={isCompleted}
                  onClick={() =>
                    navigate(`/extend-probation/${params.row.empId}`, {
                      state: {employee: params.row}
                    })
                  }
                />
              </span>
            </Tooltip>

            <Tooltip title="End Probation" arrow>
              <span>
                <GridActionsCellItem
                  key={`end-${params.id}`}
                  icon={<BlockIcon />}
                  label="End"
                  color="error"
                  disabled={isCompleted}
                  onClick={() =>
                    navigate(`/end-probation/${params.row.empId}`, {state: {employee: params.row}})
                  }
                />
              </span>
            </Tooltip>
          </Stack>
        );
      }
    }
  ];

  const fetchData = async (page, pageSize, empId, status, searchName = "") => {
    try {
      setLoading(true);
      const managerId = window.sessionStorage.getItem("empId");
      const url = `admin/getProbationList?page=${page}&size=${pageSize}&empId=${empId}&status=${status}&managerId=${managerId}&empName=${searchName}`;
      const response = await GET(url);
      const probationList = response?.data?.data?.paginatedData;
      const transformedData = probationList.map((item, index) => {
        const formatDate = dateString => {
          if (!dateString) return "";
          const date = new Date(dateString);
          return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
          });
        };

        return {
          ...item,
          id: index + 1,
          empName: `${item.firstName} ${item.lastName}`,
          status: item.probationFlag === 0 ? "Pending" : "Completed",
          designation: formatDesignation(item?.designation),
          probationStartDate: formatDate(item.probationStartDate),
          probationEndDate: formatDate(item.probationEndDate)
        };
      });
      setData(transformedData);
      setFilteredData(transformedData);
    } catch (error) {
      console.error("Error fetching probation data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, pageSize, "", "");
  }, [page, pageSize]);

  const handleSearch = () => {
    fetchData(
      page,
      pageSize,
      empId,
      status === "pending" ? 0 : status === "completed" ? 1 : "",
      searchName
    );
  };

  const onSearch = e => {
    const {value, name} = e.target;
    if (name === "empId") {
      setEmpId(value || "");
    } else if (name === "searchName") {
      setSearchName(value);
    }
  };

  const handleStatusChange = e => {
    setStatus(e.target.value);
  };

  const handlePaginationChange = newPage => {
    setPage(newPage);
     setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Probation List");
    XLSX.writeFile(workbook, "Probation_List.xlsx");
  };

  const handleEndProbation = async row => {
    const isConfirmed = window.confirm("Are you sure you want to end the probation period?");
    if (isConfirmed) {
      try {
        const url = "admin/endProbation";

        const todayDate = new Date().toISOString().split("T")[0];
        const payload = {
          empId: row.empId,
          probation_end_date: todayDate
        };
        const response = await POST(url, payload);
        if (response?.status === 200) {
          alert("Probation period ended successfully!");
          fetchData(page, pageSize, empId, status);
        } else {
          alert("Failed to end probation. Try again later.");
        }
      } catch (error) {
        alert("Something went wrong. Please try again!");
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} pb={2} px={3}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "repeat(1, 1fr)" : "repeat(12, 1fr)",
                  gap: 2
                }}
              >
                <Typography
                sx={{
                  gridColumn: "span 2",
                  color: "textPrimary",
                  fontFamily: properties.fontFamily,
                  fontSize: "15px",
                  fontWeight: "bold"
                }}
              >
                Probation List
              </Typography>
                <MDBox sx={{gridColumn: isMobile ? "span 12" : "span 2"}}>
                  <MDInput
                    value={empId}
                    onChange={onSearch}
                    name="empId"
                    label="Employee ID"
                    variant="outlined"
                    fullWidth
                  />
                </MDBox>
                <MDBox sx={{gridColumn: isMobile ? "span 12" : "span 2"}}>
                  <MDInput
                    value={searchName}
                    onChange={onSearch}
                    name="searchName"
                    label="Search by Name"
                    variant="outlined"
                    fullWidth
                  />
                </MDBox>
                <MDBox sx={{gridColumn: isMobile ? "span 12" : "span 2"}} >
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={status}
                      onChange={handleStatusChange}
                      label="Status"
                      sx={{height: "43px", display: "flex", alignItems: "center"}}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
                <MDBox
                  sx={{
                    gridColumn: isMobile ? "span 12" : "span 3",
                    display: "flex",
                    gap: 2,
                    flexDirection: isMobile ? "column" : "row"
                  }}
                >
                  <MDButton
                    variant="contained"
                    color="info"
                    onClick={handleSearch}
                    fullWidth={isMobile}
                    sx={{background: properties.colors.bgcolorbluethem}}
                  >
                    <SearchIcon /> Search
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="success"
                    onClick={handleDownloadExcel}
                    fullWidth={isMobile}
                  >
                    <DownloadIcon /> Download
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredData}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={[rowLength]}
              pagination
              page={page}
              onPageChange={handlePaginationChange}
              loading={loading}
              components={{Toolbar: GridToolbar}}
              hideFooter
              sx={{
                fontSize: "14px", 
                fontFamily: properties.fontFamily,
                height: "490px",
                fontWeight:400,
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important",
                },
              }}
            />
          </Paper>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}
